import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import Contact from '../../home/components/Contact';
import { testimonials } from '../../../mocks/testimonials';

export default function AtlantaPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const allServices = [
    'Web Design & Development',
    'AI Integration & Automation',
    'Search Engine Optimization (SEO)',
    'Social Media Marketing',
    'Google Local Services Ads (LSA)',
    'Pay-Per-Click Advertising (PPC)',
    'Reputation Management',
    'Content Marketing',
    'Email Marketing',
    'Lead Generation'
  ];

  const industries = [
    { name: 'Electrical Contractors', icon: 'ri-flashlight-line' },
    { name: 'HVAC Services', icon: 'ri-temp-cold-line' },
    { name: 'Landscaping Companies', icon: 'ri-plant-line' },
    { name: 'Pest Control Services', icon: 'ri-bug-line' },
    { name: 'Plumbing Contractors', icon: 'ri-drop-line' },
    { name: 'Roofing Companies', icon: 'ri-home-4-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Atlanta Contractor Digital Marketing Agency
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Dominate the Atlanta market with specialized digital marketing strategies designed exclusively for contractors. Generate high-quality leads and grow your contracting business in the capital of the South.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={scrollToContact} className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer">
                Get Your Free Marketing Audit <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a href="tel:2153963311" className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line mr-2"></i> (215) 396-3311
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Digital Marketing for Atlanta Contractors
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Atlanta and the surrounding metro area represent one of the fastest-growing markets in the Southeast. With over 6 million residents in the metro area, a booming economy, and continuous development, the demand for quality contracting services is exceptional across residential, commercial, and corporate sectors.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Our local expertise helps Atlanta contractors stand out in neighborhoods from Buckhead to Midtown, from Alpharetta to Marietta, ensuring you capture high-value customers in this competitive and rapidly expanding market.
              </p>
              <div className="space-y-4">
                {[
                  'Rank #1 on Google for high-value Atlanta contractor searches',
                  'Generate qualified leads from all Atlanta neighborhoods and suburbs',
                  'Build a dominant online presence in the competitive Georgia market',
                  'Attract residential, commercial, and corporate clients with proven strategies'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/4f9456683e43b39308b9a83bbe130cac.jpg"
                alt="Atlanta City Skyline"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
              Why Atlanta Contractors Choose Million Dollar Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize exclusively in contractor marketing and understand the unique opportunities of growing a contracting business in Atlanta's booming market.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-map-pin-2-line',
                title: 'Atlanta Market Expertise',
                description: 'We know the Atlanta market inside and out—from urban developments to suburban communities. We understand local search patterns and how to capture customers across the entire metro area.'
              },
              {
                icon: 'ri-team-line',
                title: 'Contractor-Only Focus',
                description: 'Unlike general marketing agencies, we only work with contractors. We understand your business model, seasonal patterns, and what it takes to generate high-quality leads in Georgia.'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Proven Results',
                description: 'Our strategies are built on data and results. We focus on metrics that matter: qualified leads, booked appointments, and revenue growth for your contracting business.'
              },
              {
                icon: 'ri-trophy-line',
                title: 'Track Record of Success',
                description: 'We\'ve helped dozens of contractors dominate their local markets and achieve consistent 6-figure and 7-figure revenues through strategic digital marketing.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Dedicated Support',
                description: 'You\'ll work directly with experienced marketing strategists who understand your business and are committed to your success in the Atlanta market.'
              },
              {
                icon: 'ri-rocket-line',
                title: 'Cutting-Edge Technology',
                description: 'We leverage the latest AI tools, automation, and marketing technology to give you a competitive advantage and maximize your marketing ROI.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-black mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Atlanta Contractor Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized digital marketing strategies for every type of contractor in the Atlanta metro area
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600 group hover:bg-red-600 cursor-pointer">
                <div className="w-14 h-14 bg-red-600 group-hover:bg-white rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <i className={`${industry.icon} text-2xl text-white group-hover:text-red-600 transition-colors`}></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-black group-hover:text-white mb-2 transition-colors">
                  {industry.name}
                </h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors">
                  Dominate Atlanta searches for {industry.name.toLowerCase()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Complete Digital Marketing Services for Atlanta Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to dominate the Atlanta contractor market and generate consistent, high-quality leads
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allServices.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-black">{service}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Success Stories from Atlanta Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped contractors across Atlanta grow their businesses with proven digital marketing strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-black">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Dominate the Atlanta Contractor Market?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your free marketing audit and discover exactly how to generate more leads, book more jobs, and grow your contracting business in Atlanta.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={scrollToContact} className="inline-flex items-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
              Claim Your Free Audit Now <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <a href="tel:2153963311" className="inline-flex items-center px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i> Call (215) 396-3311
            </a>
          </div>
        </div>
      </section>

      <div id="contact">
        <Contact />
      </div>

      <Footer />
    </div>
  );
}